import { useState } from 'react';
import { Box } from '@mui/material';
import { useStyles } from './Diagnosis.styles';
import { iconBtnPosition, iconBtnType, IconButton } from '../../../../molecules/icon-button';
import avatarIcon from '../../../../../../assets/images/medical.png';
import clsx from 'clsx';
import { Menu } from '../../../../molecules/menu';
import { colors } from '../../../../../colors';
import { sentenceCase } from 'sentence-case';
import { fontWeight, Text, textLevel } from '../../../../atoms/typography';
import { Button, btnSize, btnType } from '../../../../atoms/button';
import { updateDiagnosisRecommendations } from '../../../../../../services/report-service/reportService.service';
import { useDispatch } from 'react-redux';
import { showSnackbar } from '../../../../../../redux/modules/snackbar';

const Diagnosis = ({ onAddClick, diagnosis, onUpdate, recommendedDiagnosis = [], handleOpenReasoning, appointmentId, fetchDiagnosisRecommendations }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);

  const handleDiscard = async (item) => {
    try {
      setIsLoading(true);
      await updateDiagnosisRecommendations({ status: "DISCARDED" }, {
        appointmentId: appointmentId,
        recommendationId: item._id,
      });
      dispatch(
        showSnackbar({
          snackType: 'success',
          snackMessage: 'Diagnosis discarded successfully.',
        }),
      );
      setIsLoading(false);
      fetchDiagnosisRecommendations();
    } catch (error) {
      setIsLoading(false);
      console.error(error);
      dispatch(
        showSnackbar({
          snackType: 'error',
          snackMessage: error?.data?.error || 'Failed to discard diagnosis.',
        }),
      );
    }
  };

  const renderEmptyState = () => {
    return (
      <Box display="flex" flexDirection="column" gap="16px">
        <Box display="flex" gap="8px" alignItems="center">
          <Box width={80} maxWidth={80} maxHeight={80}>
            <img src={avatarIcon} alt="Consultation" width="100%" height="100%" />
          </Box>
          <Box display="flex" flexDirection="column" gap="4px">
            <span className={classes.bodyUuid}>Add diagnoses</span>
            <span className={classes.bodyServiceName}>A patient must have a diagnoses to bill insurance</span>
          </Box>
        </Box>
      </Box>
    );
  };

  const renderDiagnosisCard = () => {
    const activeDiagnoses =
      diagnosis?.relatedElements?.filter(item => item?.priority?.name?.trim()?.toLowerCase() === 'active') ??
      [];
    return (
      <Box display="flex" flexDirection="column" gap="12px" width="100%" height="100%">
        {recommendedDiagnosis?.map(
          (item, index) => (
            (
              <Box key={index} className={classes.card}>
                <Box flex={1} display="flex" flexDirection="column" gap="8px">
                  <Box display="flex" gap="12px" alignItems="center">
                    <Text
                      level={textLevel.L}
                      weight={fontWeight.BOLD}
                      color={colors.neutral900}
                      className={classes.cardTitle}
                    >
                      {item.diagnosisName}
                    </Text>
                    <Box className={classes.badgePotential}>{sentenceCase(item.status)}</Box>
                  </Box>
                  <Text level={textLevel.M} weight={fontWeight.MEDIUM} color={colors.neutral700}>
                    {item.notes}
                  </Text>
                  <Box display="flex" flexDirection="column" gap="8px">
                    <Text level={textLevel.M} weight={fontWeight.MEDIUM} color={colors.neutral500}>
                      {item.icd10Code}
                    </Text>
                  </Box>
                  <Box className={classes.reasoningLink}>
                    <Text
                      className={classes.viewReasoningText}
                      level={textLevel.S}
                      weight={fontWeight.MEDIUM}
                      color={colors.primary500}
                      onClick={() => handleOpenReasoning?.(item)}
                    >
                      View the reasoning
                    </Text>
                  </Box>
                </Box>
                <Box display="flex" gap="12px">
                  <Button
                    variant={btnType.OUTLINE}
                    size={btnSize.SMALL}
                    onClick={() => handleDiscard(item)}
                    className={classes.discardButton}
                    disabled={isLoading}
                  >
                    Discard
                  </Button>
                  <Button
                    variant={btnType.OUTLINE}
                    size={btnSize.SMALL}
                    onClick={() => onUpdate?.({ ...item, recommendationId: item._id, status: 'CONFIRMED' })}
                    className={classes.confirmButton}
                  >
                    Confirm
                  </Button>
                </Box>
              </Box>
            )
          ),
        )}
        {activeDiagnoses?.map((item, index) => (
          <Box key={index} className={classes.card}>
            <Box flex={1} display="flex" flexDirection="column" gap="8px">
              <Box display="flex" gap="12px" alignItems="center">
                <Text
                  level={textLevel.L}
                  weight={fontWeight.BOLD}
                  color={colors.neutral900}
                  className={classes.cardTitle}
                >
                  {item.name}
                </Text>
                <Box className={classes.badgeSuccess}>{sentenceCase(item.priority.name)}</Box>
              </Box>
              <Text level={textLevel.M} weight={fontWeight.MEDIUM} color={colors.neutral700}>
                {item.notes}
              </Text>
              <Text level={textLevel.M} weight={fontWeight.MEDIUM} color={colors.neutral500}>
                {item.selectedIct10code}
              </Text>
            </Box>
            <Menu
              icon="more"
              items={[
                {
                  label: 'Update diagnoses',
                  onClick: () => onUpdate(item),
                },
              ]}
            />
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Box className={classes.container}>
      <Box className={classes.header}>
        <Box>Diagnoses</Box>
        <Box>
          <IconButton
            className={classes.addBtn}
            icon="plus"
            variant={iconBtnType.TEXT}
            onClick={onAddClick}
            iconPosition={iconBtnPosition.LEFT}
          >
            Add new
          </IconButton>
        </Box>
      </Box>
      <Box
        className={clsx(classes.body, {
          [classes.medicationBody]: diagnosis?.relatedElements?.length !== 0,
        })}
      >
        {diagnosis?.relatedElements?.length === 0 && recommendedDiagnosis?.length === 0 ? renderEmptyState() : renderDiagnosisCard()}
      </Box>
    </Box>
  );
};
export { Diagnosis };
